import { formatShortDateTime } from "@el/core";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { Icon, type IconName } from "../icons/Icon";
import { can, ROLE_LABEL, useAuth, useMe } from "../lib/auth";
import { PRODUCT_NAME } from "../lib/labels";
import { useOutboxErrors } from "../lib/repo";
import { getSync, syncLabel, syncState, useSyncStatus } from "../lib/sync";
import { ErrorBoundary } from "./ErrorBoundary";
import { UpdatePrompt } from "./UpdatePrompt";
import { Button, usePopover } from "./ui";

type NavItem = { id: string; to: string; label: string; icon: IconName; match?: RegExp; show: boolean };

export function AppShell() {
  const me = useMe();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: ページを移ったらメニューを閉じる
  useEffect(() => setMenuOpen(false), [loc.pathname]);

  const items: NavItem[] = [
    { id: "proposals", to: "/", label: "提案", icon: "list", match: /^\/($|proposals)/, show: true },
    { id: "customers", to: "/customers", label: "顧客", icon: "customer", show: true },
    { id: "products", to: "/products", label: "設備マスタ", icon: "equipment", match: /^\/(products|processes)/, show: true },
    { id: "patterns", to: "/patterns", label: "組み合わせパターン", icon: "pattern", show: true },
    { id: "imports", to: "/imports", label: "取り込み", icon: "inbox", show: can.importFiles(me.role) },
    { id: "settings", to: "/settings", label: "設定", icon: "settings", show: can.manageRules(me.role) },
  ];

  return (
    <>
      <header className="hdr">
        <NavLink to="/" className="hdr-brand" aria-label={`${PRODUCT_NAME}のホーム`}>
          <img className="hdr-logo" src="/img/logo-agentic-white.webp" width={480} height={145} alt="Agentic" />
          <img className="hdr-mark" src="/img/mark-agentic-white.webp" width={96} height={93} alt="Agentic" />
          <span className="hdr-name">{PRODUCT_NAME}</span>
        </NavLink>
        <nav className={menuOpen ? "hdr-nav open" : "hdr-nav"} aria-label="メニュー">
          {items
            .filter((i) => i.show)
            .map((i) => {
              const active = i.match ? i.match.test(loc.pathname) : loc.pathname.startsWith(i.to);
              return (
                <NavLink key={i.to} to={i.to} data-testid={`nav-${i.id}`} className={active ? "active" : undefined} aria-current={active ? "page" : undefined}>
                  <Icon name={i.icon} size={20} />
                  {i.label}
                </NavLink>
              );
            })}
        </nav>
        <div className="hdr-right">
          <SyncIndicator />
          <UserMenu />
          <button type="button" className="hdr-btn hdr-menu-btn" aria-expanded={menuOpen} aria-label="メニュー" onClick={() => setMenuOpen((o) => !o)}>
            <Icon name={menuOpen ? "close" : "menu"} size={20} />
          </button>
        </div>
      </header>
      <UpdatePrompt />
      <ErrorBoundary resetKey={loc.pathname}>
        <Outlet />
      </ErrorBoundary>
    </>
  );
}

/** 同期の状態（ヘッダーの右に1つだけ） */
function SyncIndicator() {
  const status = useSyncStatus();
  const { text, tone } = syncLabel(status);
  const pop = usePopover();
  const errors = useOutboxErrors();
  return (
    <div
      className="hdr-user"
      ref={(el) => {
        pop.ref.current = el;
      }}
    >
      <button type="button" className="hdr-sync" data-testid="header-sync-state" data-state={syncState(status)} aria-expanded={pop.open} onClick={() => pop.setOpen((o) => !o)}>
        <span className={`dot dot-${tone}`} aria-hidden />
        <span className="nowrap">{text}</span>
      </button>
      {pop.open && (
        <div className="pop right" role="dialog" aria-label="同期の状態">
          <div className="stack-sm">
            <strong>{text}</strong>
            <span className="small muted">{status.lastSyncAt ? `最後の同期 ${formatShortDateTime(status.lastSyncAt)}` : "まだ同期していません"}</span>
            {status.message && <span className="small">{status.message}</span>}
            {errors.status === "ready" && errors.data.length > 0 && (
              <ul className="small" style={{ margin: 0, paddingLeft: "var(--sp-5)" }}>
                {errors.data.slice(0, 5).map((r) => (
                  <li key={r.opId}>{r.error}</li>
                ))}
              </ul>
            )}
            <Button size="sm" icon="sync" online onClick={() => void getSync().syncNow()}>
              今すぐ同期する
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/** 利用者の表示（ログインとログアウトは無い。デモの利用者ならその案内） */
function UserMenu() {
  const me = useMe();
  const { state } = useAuth();
  const demo = state.status === "signedIn" && !!state.demo;
  const pop = usePopover();
  return (
    <div
      className="hdr-user"
      ref={(el) => {
        pop.ref.current = el;
      }}
    >
      <button type="button" className="hdr-btn" data-testid="header-user-menu" aria-label={`利用者メニュー（${me.name}）`} aria-expanded={pop.open} onClick={() => pop.setOpen((o) => !o)}>
        <Icon name="user" size={20} />
        <span className="hdr-user-name">{me.name}</span>
        <Icon name="chevronDown" />
      </button>
      {pop.open && (
        <div className="menu">
          <div className="menu-head">
            {me.name}（{ROLE_LABEL[me.role]}）
            {!demo && (
              <>
                <br />
                {me.email}
              </>
            )}
          </div>
          {demo && (
            <p className="menu-note small muted" data-testid="header-demo">
              デモのため、ログインせずに管理者として使えます。
            </p>
          )}
        </div>
      )}
    </div>
  );
}
